// src/Routes.jsx
import React from "react";
import { Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";

// Page imports
import LoginScreen from "pages/login-screen";
import EmployerLogin from "pages/employer-login";
import RecruiterLogin from "pages/recruiter-login";
import ForgotPassword from "pages/forgot-password";
import MasterAdminDashboard from "pages/master-admin-dashboard";
import UserManagement from "pages/user-management";
import RecruiterManagement from "pages/recruiter-management";
import ClabUserBiodataReview from "pages/clab-user-biodata-review";
import RecruiterDashboard from "pages/recruiter-dashboard";
import RecruiterBiodataManagement from "pages/recruiter-biodata-management";
import EmployerBiodataSelection from "pages/employer-biodata-selection";
import MockDataManagementDashboard from "pages/mock-data-management-dashboard";
import DesignSystemThemeConfiguration from "pages/design-system-theme-configuration";

// New CLAB User pages
import ClabDashboard from "pages/clab-dashboard";
import ClabBiodataPending from "pages/clab-biodata-pending";
import ClabBiodataDrafts from "pages/clab-biodata-drafts";
import ClabBiodataApproved from "pages/clab-biodata-approved";
import ClabBiodataRejected from "pages/clab-biodata-rejected";
import ClabBiodataAmended from "pages/clab-biodata-amended";
import ClabBiodataShortlisted from "pages/clab-biodata-shortlisted";
import ClabUnselectedWorkers from './pages/clab-unselected-workers';
import ClabSelectionRequests from './pages/clab-selection-requests';
import ClabSelectionRequestDetails from './pages/clab-selection-requests/[applicationId]';
import ClabArrivalNotifications from './pages/clab-arrival-notifications';
import EmployerDashboard from './pages/employer-dashboard';
import EmployerApplicationManagement from './pages/employer-application-management';
import NewSpecialQuota from './pages/employer-special-quota/NewSpecialQuota';
import EmployerSpecialQuotaDetails from './pages/employer-special-quota/[vdrId]';
import EmployerApprovedWorkers from './pages/employer-approved-workers';
import EmployerSubmittedApplications from './pages/employer-approved-applications';
import EmployerSubmittedApplicationDetails from './pages/employer-approved-applications/details';
import EmployerShortlist from './pages/employer-shortlist';
import ClabReportsAnalytics from './pages/clab-reports-analytics';
import VDRApplicationDetails from './pages/employer-vdr-applications/[vdr_id]';
import RecruiterBiodataEdit from 'pages/recruiter-biodata-edit';
import AdminSettings from 'pages/admin-settings';
import ClabSettings from 'pages/clab-settings';
import RecruiterSettings from 'pages/recruiter-settings';
import ClabBulkVerification from 'pages/clab-bulk-verification';
import AdminSpecialQuotas from './pages/admin-special-quotas';

const AppRoutes = () => {
  return (
    <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        {/* Public Routes */}
        <Route path="/login-screen" element={<LoginScreen />} />
        <Route path="/employer-login" element={<EmployerLogin />} />
        <Route path="/recruiter-login" element={<RecruiterLogin />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/" element={<LoginScreen />} />
        
        {/* Protected Routes */}
        <Route 
          path="/master-admin-dashboard" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'master_admin']}>
              <MasterAdminDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/user-management" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'master_admin']}>
              <UserManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/recruiter-management" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'master_admin']}>
              <RecruiterManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/clab-user-biodata-review" 
          element={
            <ProtectedRoute allowedRoles={['clab', 'clab_user', 'admin', 'master_admin', '1', 1]}>
              <ClabUserBiodataReview />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/recruiter-dashboard" 
          element={
            <ProtectedRoute allowedRoles={['recruiter', 'admin']}>
              <RecruiterDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/recruiter-biodata-management" 
          element={
            <ProtectedRoute allowedRoles={['recruiter', 'admin']}>
              <RecruiterBiodataManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/recruiter-biodata-create" 
          element={
            <ProtectedRoute allowedRoles={['recruiter', 'admin']}>
              <RecruiterBiodataEdit />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/recruiter-biodata-edit/:id" 
          element={
            <ProtectedRoute allowedRoles={['recruiter', 'admin']}>
              <RecruiterBiodataEdit />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/recruiter-biodata-edit" 
          element={
            <ProtectedRoute allowedRoles={['recruiter', 'admin']}>
              <RecruiterBiodataEdit />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/employer-dashboard" 
          element={
            <ProtectedRoute allowedRoles={['employer', 'admin']}>
              <EmployerDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/employer-biodata-selection" 
          element={
            <ProtectedRoute allowedRoles={['employer', 'admin']}>
              <EmployerBiodataSelection />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/employer-application-management" 
          element={
            <ProtectedRoute allowedRoles={['employer', 'admin']}>
              <EmployerApplicationManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/employer-special-quota/new" 
          element={
            <ProtectedRoute allowedRoles={['employer', 'admin']}>
              <NewSpecialQuota />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/employer-special-quota/:vdrId" 
          element={
            <ProtectedRoute allowedRoles={['employer', 'admin']}>
              <EmployerSpecialQuotaDetails />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/employer-submitted-applications"
          element={
            <ProtectedRoute allowedRoles={['employer', 'admin']}>
              <EmployerSubmittedApplications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employer-submitted-applications/:vdrId"
          element={
            <ProtectedRoute allowedRoles={['employer', 'admin']}>
              <EmployerSubmittedApplicationDetails />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/employer-approved-workers" 
          element={
            <ProtectedRoute allowedRoles={['employer', 'admin']}>
              <EmployerApprovedWorkers />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/employer-browse-workers" 
          element={
            <ProtectedRoute allowedRoles={['employer', 'admin']}>
              <EmployerApprovedWorkers />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/employer-shortlist" 
          element={
            <ProtectedRoute allowedRoles={['employer', 'admin']}>
              <EmployerShortlist />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/employer-vdr-applications/:vdr_id" 
          element={
            <ProtectedRoute allowedRoles={['employer', 'admin']}>
              <VDRApplicationDetails />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/mock-data-management-dashboard" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MockDataManagementDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/design-system-theme-configuration" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DesignSystemThemeConfiguration />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin-settings" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'master_admin']}>
              <AdminSettings />
            </ProtectedRoute>
          } 
        />
        
        {/* New CLAB User Routes */}
        <Route 
          path="/clab-dashboard" 
          element={
            <ProtectedRoute allowedRoles={['clab', 'clab_user', 'admin', 'master_admin', '1', 1]}>
              <ClabDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/clab-biodata-pending" 
          element={
            <ProtectedRoute allowedRoles={['clab', 'clab_user', 'admin', 'master_admin', '1', 1]}>
              <ClabBiodataPending />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/clab-biodata-drafts" 
          element={
            <ProtectedRoute allowedRoles={['clab', 'clab_user', 'admin', 'master_admin', '1', 1]}>
              <ClabBiodataDrafts />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/clab-biodata-approved" 
          element={
            <ProtectedRoute allowedRoles={['clab', 'clab_user', 'admin', 'master_admin', '1', 1]}>
              <ClabBiodataApproved />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/clab-biodata-rejected" 
          element={
            <ProtectedRoute allowedRoles={['clab', 'clab_user', 'admin', 'master_admin', '1', 1]}>
              <ClabBiodataRejected />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/clab-biodata-amended" 
          element={
            <ProtectedRoute allowedRoles={['clab', 'clab_user', 'admin', 'master_admin', '1', 1]}>
              <ClabBiodataAmended />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/clab-biodata-shortlisted"
          element={
            <ProtectedRoute allowedRoles={['clab', 'clab_user', 'admin', 'master_admin', '1', 1]}>
              <ClabBiodataShortlisted />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clab-unselected-workers"
          element={
            <ProtectedRoute allowedRoles={['clab', 'clab_user', 'admin', 'master_admin', '1', '0', 1, 0]}>
              <ClabUnselectedWorkers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clab-selection-requests" 
          element={
            <ProtectedRoute allowedRoles={['clab', 'clab_user', 'admin', 'master_admin', '1', 1]}>
              <ClabSelectionRequests />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/clab-selection-requests/:applicationId"
          element={
            <ProtectedRoute allowedRoles={['clab', 'clab_user', 'admin', 'master_admin', '1', 1]}>
              <ClabSelectionRequestDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clab-arrival-notifications"
          element={
            <ProtectedRoute allowedRoles={['clab', 'clab_user', 'admin', 'master_admin', '1', '0', 1, 0]}>
              <ClabArrivalNotifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clab-reports-analytics"
          element={
            <ProtectedRoute allowedRoles={['clab', 'clab_user', 'admin', 'master_admin', '1', 1]}>
              <ClabReportsAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clab-bulk-verification"
          element={
            <ProtectedRoute allowedRoles={['clab', 'clab_user', 'admin', 'master_admin', '1', 1]}>
              <ClabBulkVerification />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clab-settings"
          element={
            <ProtectedRoute allowedRoles={['clab', 'clab_user', 'admin', 'master_admin', '1', 1]}>
              <ClabSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-special-quotas"
          element={
            <ProtectedRoute allowedRoles={['admin', 'master_admin', 'clab', 'clab_user', '1', 1]}>
              <AdminSpecialQuotas />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/recruiter-settings" 
          element={
            <ProtectedRoute allowedRoles={['recruiter', 'admin']}>
              <RecruiterSettings />
            </ProtectedRoute>
          } 
        />
        
        <Route path="*" element={<div>404 - Page Not Found</div>} />
      </RouterRoutes>
    </ErrorBoundary>
  );
};

export default AppRoutes;
